import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import InsightsReportCard from '@intelligence/components/ReportCard/InsightsReportCard';
import ReportCard from '@intelligence/components/ReportCard';
import { highlightText } from '@/pages/dashboard/components/FilterSelect/utils';
import { IntelligenceReportDataInfo } from '../../type';
import {
  convertToInsightsReportData,
  getReportTypeConfig,
  hasBulletedListTool,
  splitReportTags,
} from '../../util';
import { IS_WOA } from '@home/_config/config';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';
import { getName, extractTextFromHtml } from '@home/utils/common';

// ---- badge 判断用的纯函数（提到组件外避免每次渲染重建） ----
const normalizeTags = (tags: string[] | null | undefined): string =>
  JSON.stringify((tags ?? []).map(t => t.trim()).filter(Boolean).sort());

const normalizeStr = (s: string | null | undefined): string =>
  extractTextFromHtml(s ?? '').trim();

// 检测 HTML 中是否包含格式化标签（粗体、斜体、列表等）
// 注意：单独的 <span> 不算，只有带 style 属性的 <span style="..."> 才算格式化
const hasFormattingTags = (s: string | null | undefined): boolean =>
  /<(strong|em|b|i|u|s|del|mark|sub|sup|ul|ol|li)\b/i.test(s ?? '')
  || /<span\s[^>]*style\s*=/i.test(s ?? '');

// 比较 studio 与原文是否引入了"新的"格式（粗体/斜体/列表/带 style 的 span）
// - studio 有格式 + 原文没有 → 算变更
// - 都有格式（哪怕标签集合不同）暂不细究，避免误报
// - studio 没有格式 → 不算格式变更（即便原文有）
const hasNewFormatting = (
  studioIntro: string | null | undefined,
  originalIntro: string | null | undefined,
): boolean => hasFormattingTags(studioIntro) && !hasFormattingTags(originalIntro);

interface ReportCardListProps {
  /** 报告列表数据 */
  reports: IntelligenceReportDataInfo[];
  /** 是否英文 */
  isEn: boolean;
  /** 搜索关键词 */
  searchKeyword: string;
  /** Studio Code */
  studioCode: string;
  /** Excel下载处理函数 */
  onDownloadExcel: (id: string, name: string) => Promise<boolean>;
  /** 点赞变化处理函数 */
  onLikeChange?: (resourceId: string, likeCounts?: number) => void;
  /** 是否显示悬浮编辑按钮 */
  showEditButton?: boolean;
  /** Toggle 是否开启（用于显示 Editor's notes 标记） */
  useEditorNotes?: boolean;
  /** 点击编辑按钮回调 */
  onEditReport?: (report: IntelligenceReportDataInfo) => void;
  /** 编辑笔记工具栏扩展（含 bulletedList 时展示列表圆点） */
  extraToolList?: string[];
  /** 摘要超过 3 行显示省略号 + More，点击展开 */
  summaryExpandable?: boolean;
  /** 标签分隔符正则（字符串形式），用于拆分含特殊符号的标签 */
  tagSeparatorReg?: string;
}

/**
 * 报告卡片列表组件
 * 根据不同的 system 类型渲染不同的卡片
 */
const ReportCardList: React.FC<ReportCardListProps> = ({
  reports,
  isEn,
  searchKeyword,
  studioCode,
  onDownloadExcel,
  onLikeChange,
  showEditButton = false,
  useEditorNotes = false,
  onEditReport,
  extraToolList = [],
  summaryExpandable = false,
  tagSeparatorReg,
}) => {
  const enableBulletedList = hasBulletedListTool(extraToolList);
  if (reports.length === 0) {
    return (
      <div className="dbh-report-card-list-empty">
        {getName(isEn, 'No report available', '暂无报告')}
      </div>
    );
  }

  const buildTopRightRender = (report: IntelligenceReportDataInfo): (() => React.ReactNode) | undefined => {
    if (!showEditButton) return undefined;
    return function TopRightContent() {
      return (
        <span
          className="dbh-report-card-overlay__edit-btn"
          onClick={(e) => {
            e.stopPropagation();
            onEditReport?.(report);
          }}
        >
          <SvgIcon className="dbh-report-card-overlay__edit-icon" type="edit-icon" />
          {isEn ? 'Edit Notes' : '编辑笔记'}
        </span>
      );
    };
  };

  const buildBadgeRender = (report: IntelligenceReportDataInfo): (() => React.ReactNode) | undefined => {
    if (!useEditorNotes) return undefined;

    let hasChanged = false;
    if (isEn) {
      const studioTagEn = report.studio_tag_en;
      const studioIntroEn = report.studio_introduction_en;
      const originalTagEn = report._original_tag_en ?? report.tag_en;
      const originalIntroEn = report._original_introduction_en ?? report.introduction_en;
      const tagChanged = studioTagEn != null
        && normalizeTags(studioTagEn) !== normalizeTags(originalTagEn);
      const studioText = normalizeStr(studioIntroEn);
      const hasStudioText = studioText !== '';
      const textChanged = hasStudioText && studioText !== normalizeStr(originalIntroEn);
      const formatChanged = hasStudioText && hasNewFormatting(studioIntroEn, originalIntroEn);
      hasChanged = tagChanged || textChanged || formatChanged;
    } else {
      const studioTag = report.studio_tag;
      const studioIntro = report.studio_introduction;
      const originalTag = report._original_tag ?? report.tag;
      const originalIntro = report._original_introduction ?? report.introduction;
      const tagChanged = studioTag != null
        && normalizeTags(studioTag) !== normalizeTags(originalTag);
      const studioText = normalizeStr(studioIntro);
      const hasStudioText = studioText !== '';
      const textChanged = hasStudioText && studioText !== normalizeStr(originalIntro);
      const formatChanged = hasStudioText && hasNewFormatting(studioIntro, originalIntro);
      hasChanged = tagChanged || textChanged || formatChanged;
    }
    if (!hasChanged) return undefined;
    return function BadgeContent() {
      return (
        <div className="dbh-report-card-badge-wrap">
          <span className="dbh-report-card-badge">
            <SvgIcon className="dbh-report-card-badge__icon" type="pencil" />
            {isEn ? "Editor's notes" : '编辑笔记'}
          </span>
        </div>
      );
    };
  };

  return (
    <div
      className={classNames('dbh-report-card-list', {
        'dbh-report-card-list--rich-list': enableBulletedList,
        'dbh-report-card-list--summary-expandable': summaryExpandable,
      })}
    >
      {reports.map((report) => {
        // 如果 system 是 "intelligence"，使用 ReportCard 渲染
        if (report.system?.toLowerCase() === 'intelligence') {
          const title = getName(isEn, report.title_en, report.title);
          const tags = splitReportTags(
            getName(isEn, report.tag_en, report.tag),
            tagSeparatorReg,
          );
          const keyword = searchKeyword?.trim();
          const reportCardData = {
            id: report.id,
            resource_id: report.resource_id,
            // 应用搜索高亮到标题
            title: keyword ? highlightText(title, keyword) : title,
            originTitle: title,
            coverImg: report.cover,
            summary: getName(isEn, report.introduction_en, report.introduction),
            // 应用搜索高亮到标签列表
            labelList: keyword
              ? (tags || []).map((tag: string) => highlightText(tag, keyword))
              : tags,
            time: moment(report.create_time).format('YYYY-MM-DD'),
            type: getName(isEn, report.report_type_en, report.report_type),
            source: report.source,
            attachment: report.attachment,
            pdf: report.pdf,
            pdf_en: report.pdf_en,
          };

          return (
            <ReportCard
              hasPDF
              key={report.resource_id}
              data={reportCardData}
              systemId={report.system}
              studioId={studioCode}
              type={'Market'}
              isNew={!!moment(report.create_time).isAfter(moment().subtract(1, 'month'))}
              handleDownloadRun={onDownloadExcel}
              reportTypeConfig={getReportTypeConfig(report.system)}
              allowHtmlInLabels={true}
              topRightRender={buildTopRightRender(report)}
              badgeRender={buildBadgeRender(report)}
              summaryExpandable={summaryExpandable}
            />
          );
        }

        // 其他 system 使用 InsightsReportCard
        return (
          <InsightsReportCard
            key={report.resource_id}
            // @ts-ignore
            data={convertToInsightsReportData(report, searchKeyword, tagSeparatorReg)}
            lang={isEn ? 0 : 1}
            shareBack={''}
            reportTypeConfig={getReportTypeConfig(report.system)}
            pdfPreviewDownloadPage="/pdfPreviewDownloadLikeShare"
            onChange={onLikeChange || (() => {})}
            showContactInfo={!IS_WOA}
            needPopover={!summaryExpandable}
            summaryExpandable={summaryExpandable}
            isNew={!!moment(report.create_time).isAfter(moment().subtract(1, 'month'))}
            topRightRender={buildTopRightRender(report)}
            badgeRender={buildBadgeRender(report)}
          />
        );
      })}
    </div>
  );
};

export default ReportCardList;
